<template>
    <div class="automation-contact-selector" v-animate-css="{ classes: 'fadeInDown', delay: 0, duration: 300 }">
        <div class="left">
            <h3>{{ $t("message.filters") }}</h3>
            <div v-for="(group, index) of clone.groups" :key="group.id">
                <FilterGroup
                    :on-removed="onFilterGroupRemoved"
                    v-model="clone.groups[index]"
                    :show-remove-button="clone.groups.length > 1"
                    :allowed-contexts="['contact']"
                    :attribute-context-prefix="''"
                />
                <div
                    class="filter-condition-wrapper"
                    v-if="clone.groups.length > 1 && index !== clone.groups.length - 1"
                >
                    <div class="filter-condition">
                        <a-select
                            :value="clone.operator"
                            @change="(op) => (clone.operator = op)"
                            @click="
                                (e) => {
                                    e.stopPropagation();
                                }
                            "
                            @mouseenter="() => (selectHovered = true)"
                            @mouseleave="() => (selectHovered = false)"
                            :class="{ hovered: selectHovered }"
                        >
                            <a-select-option value="or">{{ $t("chatbotv2.or") }}</a-select-option>
                            <a-select-option value="and"> {{ $t("chatbotv2.and") }}</a-select-option>
                        </a-select>
                    </div>
                </div>
            </div>
            <div class="filter-add-group-wrapper">
                <div class="filter-add-group" @click="addFilterGroup">
                    <div class="filter-add-group-plus">
                        <span class="material-icons"> add </span>
                    </div>
                    <span>{{ $t("chatbotv2.addFilterGroup") }}</span>
                </div>
            </div>
        </div>
        <div class="right">
            <SupsisTable
                :columns="getCols()"
                :rows="items"
                empty-data-bordered
                :top-pagination="!singleResultMode"
                :bottom-pagination="!singleResultMode"
                :pagination-cursor="pagination"
                :options="{
                    checkboxOption,
                    rowStyleOption,
                    clipboardOption,
                    fixedHeader: true,
                    id: 'automation-contacts-table',
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { generateRandomId } from "../../../../../../utils/session";
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import { CONTACT_API } from "../../../../../../api/visitor";
import SupsisTable from "../../../../../../components/SupsisTable.vue";
import HoverMenuCell from "../../../../../../components/common/HoverMenuCell.vue";
import moment from "moment/moment";
import FilterGroup from "../blocks/filter/FilterGroup.vue";

const log = log2("AutomationContactSelector");
export default Vue.extend({
    name: "AutomationContactSelector",
    components: { SupsisTable, FilterGroup, SupsisButton },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                groups: [],
                operator: "or",
            }),
        },
        singleResultMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            filterAddCtx: null,
            selectHovered: false,
            loading: false,
            items: [],
            total: 0,
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            fullScreen: false,
            downloading: false,
            checkboxOption: {
                selectedRowKeys: [],
                // row select change event
                selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
                    console.log("selectedRowChange", { row, isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
                // selected all change event
                selectedAllChange: ({ isSelected, selectedRowKeys }) => {
                    console.log("selectedAllChange", { isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
        };
    },
    methods: {
        isFilterExists(attribute) {
            return this.clone.groups.some((group) => group.filters.some((filter) => filter.attribute === attribute));
        },
        addFilter(attribute, value) {
            this.clone.groups[0].filters.push({
                attribute,
                value,
                rule: "equals",
            });
        },
        removeFilter(attribute) {
            this.clone.groups[0].filters = this.clone.groups[0].filters.filter(
                (filter) => filter.attribute !== attribute
            );
        },
        getCols() {
            return [
                {
                    field: "fullname",
                    key: "profilePicThumbObj",
                    title: this.$t("message.profile"),
                    width: 50,
                    align: "center",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "a-avatar",
                            {
                                props: {
                                    size: 30,
                                    src: row.instagramInfo?.profile_pic,
                                    username: row.fullname,
                                    shape: "square",
                                },
                                style: "background-color: #3899ff; border-radius: 7px !important",
                            },
                            (row.fullname || "").substr(0, 1).toLocaleUpperCase()
                        );
                    },
                },
                {
                    key: "fullname",
                    field: "fullname",
                    title: this.$t("message.name"),
                    width: "",
                    align: "left",
                },
                {
                    key: "phone",
                    field: `phone`,
                    title: this.$t("message.number"),
                },
                {
                    key: "email",
                    field: `email`,
                    title: this.$t("message.email"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            HoverMenuCell,
                            {
                                props: {
                                    filterAdd: !this.isFilterExists("email"),
                                    filterRemove: this.isFilterExists("email"),
                                    copy: true,
                                },
                                on: {
                                    filterAdded: () => {
                                        this.addFilter("email", row.email);
                                    },
                                    filterRemoved: () => {
                                        this.removeFilter("email");
                                    },
                                },
                            },
                            [h("span", row.email || "-")]
                        );
                    },
                },
                {
                    key: "createdAt",
                    field: `createdAt`,
                    title: this.$t("automation.createdAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.createdAt).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
            ];
        },
        fetch() {
            this.loading = true;
            const isEmpty = this.clone.groups.every((group) => group.filters.length === 0);
            if (isEmpty) {
                this.loading = false;
                this.items = [];
                return;
            }
            const cursor = {
                offset: this.singleResultMode ? 0 : (this.pagination.page - 1) * this.pagination.perPage,
                limit: this.singleResultMode ? 1 : this.pagination.perPage,
            };
            CONTACT_API.listContactsByFilterGroups(this.clone, cursor, [])
                .then((data) => {
                    const { items, count } = data;
                    this.items = items;
                    this.total = count;
                    this.pagination.total = count;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onFilterGroupRemoved(id) {
            this.clone.groups = this.clone.groups.filter((group) => group.id !== id);
        },
        addFilterGroup() {
            const id = generateRandomId();
            this.clone.groups.push({
                id,
                operator: "and",
                filters: [],
            });
        },
        downloadAsFile() {
            this.downloading = true;
            // TICIMAX_API.listExampleMembersByFilterGroups(this.clone, {}, true)
            //     .then((res) => res.data)
            //     .then((data) => {
            //         const { s3Key } = data;
            //         const url = `https://static.supsis.live/${s3Key}`;
            //         window.open(url, "_blank");
            //         this.downloading = false;
            //     });
        },
    },
    mounted() {
        log("mounted", this.value);
        this.fetch();
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.fetch();
                this.$emit("input", newValue);
            },
        },
        pagination: {
            handler() {
                this.fetch();
            },
            deep: true,
        },
    },
});
</script>

<style lang="scss">
.automation-contact-selector {
    display: flex;
    box-shadow: 0 4px 20px #00000040;
    background: #ffffff;
    padding: 25px;
    border-radius: 6px;
    height: calc(100vh - 100px);
    overflow: auto;
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 5px;

    .right {
        .preview-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }

    .left {
        width: 300px;
        border-right: 1px solid #ccc;
        min-height: 500px;
        padding-right: 10px;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .right {
        width: calc(100% - 300px);
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .filter-condition-wrapper {
        align-items: center;
        display: flex;
        justify-content: center;
        width: 100%;
        margin-top: 30px;

        &:before {
            border-top: 1px solid #ccc;
            content: "";
            flex-grow: 1;
            height: 0;
            width: 0;
        }

        &:after {
            border-top: 1px solid #ccc;
            content: "";
            flex-grow: 1;
            height: 0;
            width: 0;
        }

        .filter-condition {
            margin: 0px 25px;
            width: 80px;

            .ant-select {
                &.hovered {
                    .ant-select-selection {
                        border-color: #40a9ff !important;
                        border-right-width: 1px !important;
                        background: #40a9ff52 !important;
                    }
                }
            }
        }
    }

    .filter-add-group-wrapper {
        align-items: center;
        display: flex;
        height: 40px;
        justify-content: center;
        width: 100%;

        &:before {
            border-top: 1px solid #ccc;
            content: "";
            flex-grow: 1;
            height: 0;
            width: 0;
        }

        &:after {
            border-top: 1px solid #ccc;
            content: "";
            flex-grow: 1;
            height: 0;
            width: 0;
        }

        .filter-add-group {
            cursor: pointer;
            display: flex;
            margin: 0 10px;
            transition: opacity 0.3s;

            span {
                color: #475e7a;
                font-weight: 600;
            }

            .filter-add-group-plus {
                align-items: center;
                background: #fff;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                display: flex;
                height: 22px;
                justify-content: center;
                margin-right: 10px;
                transition: background-color 0.3s;
                width: 22px;

                &:hover {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #006cff;
                    font-size: 12px;
                    margin-left: 1px;
                    transition: color 0.6s;
                }
            }
        }
    }
}
</style>
<style lang="scss">
#automation-contacts-table {
    .ve-table-body-td {
        padding: 0 2px !important;
    }

    .actions {
        display: flex;
        gap: 5px;

        .supsis-button {
            background: none;
            border: none;
            padding: 3px;

            &.instagram {
                width: 25px;
                height: 25px;
            }

            svg {
                width: 20px;
                height: 20px;
            }
        }
    }
}
</style>
